		/**
		 * @ngdoc service
		 * @constructor
		 * @name WebCoreModule.service:WcHttpEndpointPrototype
		 * @requires WebCoreModule.service:WcHttpRequestService
		 * @param {String} route The url endpoint string to use for this endpoint. This value will be appended to the application's base url.
		 * @param {String} baseURL If passed in, this base url will be used instead of the value set application-wide.
		 * @returns {WcHttpEndpointPrototype} The prototype created with the passed in information. The returned WcHttpEndpointPrototype
		 * will expose the following properties and methods:
		 * @description
		 * The WcHttpEndpointPrototype wraps the WcHttpRequestService to make calls against a restful endpoint easy. Creating a new
		 * endpoint prototype exposes all http verbs for that endpoint, making calls to the restful service easy to manage.
		 *
		 * To leverage the WcHttpEndpointPrototype, create a new instance using 'new WcHttpEndpointPrototype()' in calling code.
		 * The WcHttpEndpointPrototype constructor creates a new instance of the WcHttpEndpointPrototype. This prototype
		 * establishes the url for an endpoint and provides functions to call for the HTTP verbs (GET, PUT, POST, DELETE).
		 */

angular.module('WebCoreModule')
	.factory('WcHttpEndpointPrototype', ['WcHttpRequestService', function(WcHttpRequestService) {
		/**
		 * @ngdoc property
		 * @name WcHttpEndpointPrototype.route
		 * @propertyOf WebCoreModule.service:WcHttpEndpointPrototype
		 * @returns {String} String defining the endpoint route to append to the baseURL for this endpoint.
		 * @description
		 * Passing in the required route string for an endpoint will establish the URL this endpoint should operate on
		 * by appending the route to the end of the baseURL.
		 */
		/**
		 * @ngdoc property
		 * @name WcHttpEndpointPrototype.baseURL
		 * @propertyOf WebCoreModule.service:WcHttpEndpointPrototype
		 * @returns {String} String defining a baseURL to override the application wide baseURL for this endpoint.
		 * @description
		 * Passing in an optional baseURL allows the user to override the baseURL for this endpoint with the passed in baseURL.
		 */
		function Endpoint(route, baseURL) {
			this.baseUrl = baseURL;
			this.route = route;
			if(baseURL) {
				this.route = this.baseUrl+this.route;
			}
		}

		Endpoint.prototype = {
			subRoute: function(id) {
				return new Endpoint(this.route + '/' + id);
			},
			/**
			 * @ngdoc method
			 * @name WcHttpEndpointPrototype.get
			 * @methodOf WebCoreModule.service:WcHttpEndpointPrototype
			 * @param {Object} options Options object to configure what happens when this verb is called. Config properties that can be set in this object include:
			 *  - cache: set to 'localStorage' or 'sessionStorage' to enable caching for this call
			 *  - alwaysRefresh: set to true to have the request always directed to the server when operating online. Each time the results are cached, making them available for offline usage. set to false to have only the first request directed to the server when operating online. Every subsequent request, online or offline, will be retrieved from the cache.
			 * @returns {Promise} The data retreived from the backing service wrapped in a promise.
			 * @description
			 * The WcHttpEndpointPrototype provides a get function to trigger an HTTP GET against the established endpoint URL.
			 *
			 * A get call can be configured to cache the returned results from the server call. These results can be stored in
			 * localStorage or session storage. Caching behavior can also be configured to happen in one of two ways:
			 *  - An always refreshing cache will always go to the server when operating online, storing a copy of the data retrieved in the specified cache.
			 *  - A non refreshing cache will go to the server with the first request when operating online, caching the results, but all subsequent requests will be retrieved from the cache, not from the server.
			 */
			get: function(options) {
				return WcHttpRequestService.get(this.route, options);
			},
			/**
			 * @ngdoc method
			 * @name WcHttpEndpointPrototype.post
			 * @methodOf WebCoreModule.service:WcHttpEndpointPrototype
			 * @param {Object} data Data to send to the endpoint when this verb is called.
			 * @param {Object} options Options object to configure what happens when this verb is called.
			 * @returns {Promise} The data retreived from the backing service wrapped in a promise.
			 * @description
			 * The WcHttpEndpointPrototype provides a post function to trigger an HTTP POST against the established endpoint URL.
			 */
			post: function(data, options) {
				return WcHttpRequestService.post(this.route, data, options);
			},
			/**
			 * @ngdoc method
			 * @name WcHttpEndpointPrototype.put
			 * @methodOf WebCoreModule.service:WcHttpEndpointPrototype
			 * @param {String} id Identifier for which record we should operate on when this verb is called.
			 * @param {Object} data Data to send to the endpoint when this verb is called.
			 * @param {Object} options Options object to configure what happens when this verb is called.
			 * @returns {Promise} The data retreived from the backing service wrapped in a promise.
			 * @description
			 * The WcHttpEndpointPrototype provides a put function to trigger an HTTP PUT against the established endpoint URL.
			 */
			put: function(id, data, options) {
				return WcHttpRequestService.put(this.route+'/'+id, data, options);
			},
			/**
			 * @ngdoc method
			 * @name WcHttpEndpointPrototype.delete
			 * @methodOf WebCoreModule.service:WcHttpEndpointPrototype
			 * @param {String} id Identifier for which record we should operate on when this verb is called.
			 * @param {Object} options Options object to configure what happens when this verb is called.
			 * @returns {Promise} The data retreived from the backing service wrapped in a promise.
			 * @description
			 * The WcHttpEndpointPrototype provides a delete function to trigger an HTTP DELETE against the established endpoint URL.
			 */
			delete: function(options){
				return WcHttpRequestService.delete(this.route, options);
			}
		};

		return Endpoint;
	}]);